'use client';

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, A11y } from "swiper/modules";
import 'swiper/css';
import 'swiper/css/navigation';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { fetchCollections, type Collection } from "@/lib/collections";
import { AnimateOnView } from "@/components/ui/AnimateOnView";

/**
 * Fisher-Yates shuffle algorithm for randomizing array
 */
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export default function CollectionsSlider() {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadCollections = async () => {
      try {
        // Carregar todas as coleções disponíveis (limite alto para pegar todas)
        const result = await fetchCollections(1, 100);
        const allCollections = result.collections;
        
        // Separar destacadas das demais
        const featured = allCollections.filter((c) => c.isFeatured);
        const nonFeatured = allCollections.filter((c) => !c.isFeatured);
        
        // Embaralhar as não-destacadas para variedade a cada carregamento
        const shuffledNonFeatured = shuffleArray(nonFeatured);
        
        // Concatenar: destacadas primeiro, depois as demais embaralhadas
        const orderedCollections = [...featured, ...shuffledNonFeatured];
        
        setCollections(orderedCollections);
      } catch (error) {
        console.error('Failed to fetch collections:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCollections();
  }, []);

  const sliderCollections = useMemo(() => collections, [collections]);
  const loopedCollections = useMemo(() => {
    if (!sliderCollections.length) {
      return [] as Array<{ collection: Collection; repeatIndex: number }>;
    }

    const MINIMUM_SLIDES = 8;
    if (sliderCollections.length >= MINIMUM_SLIDES) {
      return sliderCollections.map((collection) => ({ collection, repeatIndex: 0 }));
    }

    const repeats = Math.ceil(MINIMUM_SLIDES / sliderCollections.length);
    const extended: Array<{ collection: Collection; repeatIndex: number }> = [];

    for (let repeatIndex = 0; repeatIndex < repeats; repeatIndex += 1) {
      sliderCollections.forEach((collection) => {
        extended.push({ collection, repeatIndex });
      });
    }

    return extended;
  }, [sliderCollections]);

  if (isLoading) {
    return (
      <section id="colecoes" className="mt-4 sm:mt-16">
        <AnimateOnView className="flex flex-col gap-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-xl sm:text-3xl font-semibold text-white">Coleções em destaque</h2>
              <p className="mt-2 max-w-xl text-sm text-white/70 hidden sm:block">
                Carregando coleções...
              </p>
            </div>
          </div>
        </AnimateOnView>
      </section>
    );
  }

  if (!loopedCollections.length) {
    return null; // Don't render if no collections
  }

  return (
    <section id="colecoes" className="mt-4 sm:mt-16">
      <AnimateOnView className="flex flex-col gap-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-xl sm:text-3xl font-semibold text-white">Coleções em destaque</h2>
            <p className="mt-2 max-w-xl text-sm text-white/70 hidden sm:block">
              Curadoria com foco em impacto visual, prontos para integrar seu fluxo de criação.
            </p>
          </div>
          <a
            href="/colecoes"
            className="inline-flex items-center gap-2 text-sm font-semibold text-brand-secondary transition-transform hover:translate-x-1 hover:text-white"
          >
            Ver todas as coleções
            <FontAwesomeIcon icon={faArrowRight} className="h-4 w-4" />
          </a>
        </div>

        <div className="relative">
          <Swiper
            modules={[Navigation, Autoplay, A11y]}
            navigation
            autoplay={{ delay: 4500, disableOnInteraction: false, pauseOnMouseEnter: true }}
            loop={loopedCollections.length > 0}
            loopAdditionalSlides={sliderCollections.length}
            spaceBetween={8}
            slidesPerView="auto"
          >
            {loopedCollections.map(({ collection, repeatIndex }, index) => (
              <SwiperSlide key={`${collection.id}-${repeatIndex}-${index}`} className="!h-auto !w-auto">
                <Link href={`/colecoes/${collection.slug}`} className="relative w-[200px] sm:w-[220px] lg:w-[240px] block">
                  <div className="group relative overflow-hidden rounded-[16px] border border-white/10 bg-white/5 p-1 shadow-[0_18px_60px_-30px_rgba(15,0,36,0.8)] transition-transform hover:-translate-y-1">
                    <div className="grid grid-cols-2 gap-0.5">
                      {collection.previews.slice(0, 4).map((preview, index) => (
                        <div
                          key={`${collection.id}-preview-${index}`}
                          className="relative aspect-square overflow-hidden rounded-md"
                        >
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img
                            src={preview}
                            alt={`Prévia da coleção ${collection.title}`}
                            className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-transparent to-black/35 opacity-0 transition-opacity duration-300 group-hover:opacity-60" />
                        </div>
                      ))}
                    </div>
                    <div className="pointer-events-none absolute inset-0 flex items-end justify-end p-2">
                      <span className="rounded-full bg-black/70 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.25em] text-white">
                        {collection.assetsCount} assets
                      </span>
                    </div>
                  </div>
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </AnimateOnView>
    </section>
  );
}
